import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'
import Logo from '@/components/generals/logo'
import MainContainer from '@/components/generals/main-container'
import MobileMenu from '@/components/generals/mobile-menu'
import { siteConfig } from '@/constants/site'

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [menuOpen, setMenuOpen] = useState(false)
    const menuButtonRef = useRef<HTMLButtonElement>(null)

    /* The route pattern (/journal/[slug]) is identical on server and client,
       including on the 404 page, so the active state never mismatches. */
    const path = router.pathname

    const closeMenu = useCallback(() => setMenuOpen(false), [])

    useEffect(() => {
        router.events.on('routeChangeStart', closeMenu)

        return () => router.events.off('routeChangeStart', closeMenu)
    }, [router.events, closeMenu])

    return (
        <header className='sticky top-0 z-40 border-b border-border bg-background'>
            <MainContainer className='flex h-16 items-center justify-between gap-6 md:h-[4.5rem]'>
                <Logo />

                <nav aria-label='Main' className='hidden md:block'>
                    <ul className='flex items-center gap-7 lg:gap-10'>
                        {siteConfig.nav.map((item) => {
                            const active = path === item.href || path.startsWith(`${item.href}/`)

                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'link-grow inline-flex min-h-11 items-center text-[0.9375rem] transition-colors duration-200 hover:text-brand',
                                            active ? 'font-medium text-foreground' : 'text-foreground/80'
                                        )}
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <button
                    ref={menuButtonRef}
                    type='button'
                    onClick={() => setMenuOpen(true)}
                    aria-expanded={menuOpen}
                    aria-controls='mobile-menu'
                    className='-mr-2 inline-flex min-h-11 items-center gap-2.5 rounded-sm px-2 font-mono text-label uppercase md:hidden'
                >
                    Menu
                    <span aria-hidden='true' className='flex w-5 flex-col gap-[5px]'>
                        <span className='h-px w-full bg-foreground' />
                        <span className='h-px w-3/5 self-end bg-foreground' />
                    </span>
                </button>
            </MainContainer>

            <MobileMenu
                open={menuOpen}
                onClose={closeMenu}
                path={path}
                returnFocusRef={menuButtonRef}
            />
        </header>
    )
}

export default SiteHeader
